import React from 'react';
import { cn } from '../lib/utils';
import { Badge } from './Badge';
import { Skeleton } from './Skeleton';

interface StatCardProps {
    title: string;
    value: string;
    change?: string;
    period?: string;
    icon?: React.ReactNode;
    className?: string;
    loading?: boolean;
}

export function StatCard({ title, value, change, period, icon, className, loading }: StatCardProps) {
    const positive = !change || change.startsWith('+') || change === 'N/A';
    return (
        <div
            className={cn(
                'rounded-2xl bg-card p-5 shadow-sm',
                className
            )}
        >
            <div className="flex items-center justify-between">
                <div className="text-sm/6 font-medium text-muted-foreground">{title}</div>
                {icon && (
                    <div className="flex size-9 items-center justify-center rounded-full bg-brand-teal/10 text-brand-teal">
                        {icon}
                    </div>
                )}
            </div>
            {loading ? (
                <Skeleton className="mt-2 h-8 w-16" />
            ) : (
                <div className="mt-2 text-3xl/8 font-semibold text-card-foreground">{value}</div>
            )}
            {loading && change !== undefined ? (
                <Skeleton className="mt-3 h-5 w-24" />
            ) : (
                change && (
                    <div className="mt-3 inline-flex items-center gap-2 text-xs/6">
                        <Badge color={positive ? 'green' : 'red'}>{change}</Badge>
                        {period && <span className="text-muted-foreground">from {period}</span>}
                    </div>
                )
            )}
        </div>
    );
}

interface HighlightedStatCardProps {
    title: string;
    value: string;
    caption?: string;
    icon?: React.ReactNode;
    className?: string;
    loading?: boolean;
}

export function HighlightedStatCard({ title, value, caption, icon, className, loading }: HighlightedStatCardProps) {
    return (
        <div
            className={cn(
                'rounded-2xl bg-primary p-5 text-primary-foreground shadow-sm',
                className
            )}
        >
            <div className="flex items-center justify-between">
                <div className="text-sm/6 font-medium text-primary-foreground/80">{title}</div>
                {icon && (
                    <div className="flex size-9 items-center justify-center rounded-full bg-white/15">
                        {icon}
                    </div>
                )}
            </div>
            {loading ? (
                <Skeleton className="mt-2 h-8 w-16 bg-white/25" />
            ) : (
                <div className="mt-2 text-3xl/8 font-semibold">{value}</div>
            )}
            {loading && caption !== undefined ? (
                <Skeleton className="mt-3 h-4 w-32 bg-white/25" />
            ) : (
                caption && <div className="mt-3 text-xs/6 text-primary-foreground/80">{caption}</div>
            )}
        </div>
    );
}
